import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, Paper } from '@mui/material'
import CropIcon from '@mui/icons-material/Crop'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { NonInteractiveTooltip } from '../custom'

type SelectionRect = { x: number; y: number; w: number; h: number }
type TextAnnotation = { x: number; y: number; text: string }

type ToolMode = 'select'

function renderCanvasWithAnnotations(
    baseCanvas: HTMLCanvasElement,
    annotations: TextAnnotation[],
    selectionRect: SelectionRect | null
): HTMLCanvasElement {
    const out = document.createElement('canvas')
    out.width = baseCanvas.width
    out.height = baseCanvas.height
    const ctx = out.getContext('2d')
    if (!ctx) return out
    ctx.drawImage(baseCanvas, 0, 0)
    ctx.font = '16px sans-serif'
    ctx.fillStyle = '#ff0000'
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2
    annotations.forEach(({ x, y, text }) => {
        ctx.strokeText(text, x, y)
        ctx.fillText(text, x, y)
    })
    if (selectionRect && selectionRect.w > 0 && selectionRect.h > 0) {
        ctx.strokeStyle = 'rgba(25, 118, 210, 0.8)'
        ctx.lineWidth = 2
        ctx.strokeRect(selectionRect.x, selectionRect.y, selectionRect.w, selectionRect.h)
    }
    return out
}

function cropCanvasToBlob(sourceCanvas: HTMLCanvasElement, rect: SelectionRect): Promise<Blob> {
    const { x, y, w, h } = rect
    const cropped = document.createElement('canvas')
    cropped.width = Math.max(1, Math.round(w))
    cropped.height = Math.max(1, Math.round(h))
    const ctx = cropped.getContext('2d')
    if (!ctx) return Promise.reject(new Error('Canvas context not available'))
    ctx.drawImage(
        sourceCanvas,
        Math.max(0, x),
        Math.max(0, y),
        cropped.width,
        cropped.height,
        0,
        0,
        cropped.width,
        cropped.height
    )
    return new Promise((resolve, reject) => {
        cropped.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))), 'image/png')
    })
}

type Props = {
    canvas: HTMLCanvasElement
    onDone: (blob: Blob) => void
    onCancel: () => void
}

export function ScreenshotEditorOverlay({ canvas, onDone, onCancel }: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const displayCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const [toolMode] = useState<ToolMode>('select')
    const [annotations] = useState<TextAnnotation[]>([])
    const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null)
    const selectionStart = useRef<{ x: number; y: number } | null>(null)

    const getCoords = useCallback(
        (clientX: number, clientY: number): { x: number; y: number } | null => {
            if (!containerRef.current) return null
            const rect = containerRef.current.getBoundingClientRect()
            const scaleX = canvas.width / rect.width
            const scaleY = canvas.height / rect.height
            return {
                x: (clientX - rect.left) * scaleX,
                y: (clientY - rect.top) * scaleY
            }
        },
        [canvas]
    )

    const redraw = useCallback(() => {
        if (!displayCanvasRef.current) return
        const composed = renderCanvasWithAnnotations(canvas, annotations, selectionRect)
        const ctx = displayCanvasRef.current.getContext('2d')
        if (!ctx) return
        ctx.clearRect(0, 0, displayCanvasRef.current.width, displayCanvasRef.current.height)
        ctx.drawImage(composed, 0, 0)
    }, [canvas, annotations, selectionRect])

    useEffect(() => {
        redraw()
    }, [redraw])

    useEffect(() => {
        if (!containerRef.current || !displayCanvasRef.current) return
        displayCanvasRef.current.width = canvas.width
        displayCanvasRef.current.height = canvas.height
        redraw()
    }, [canvas, redraw])

    useEffect(() => {
        if (!canvas) return
        const onMouseMove = (e: MouseEvent) => {
            if (toolMode !== 'select' || !selectionStart.current) return
            const coords = getCoords(e.clientX, e.clientY)
            if (coords) {
                setSelectionRect({
                    x: Math.min(selectionStart.current.x, coords.x),
                    y: Math.min(selectionStart.current.y, coords.y),
                    w: Math.abs(coords.x - selectionStart.current.x),
                    h: Math.abs(coords.y - selectionStart.current.y)
                })
            }
        }
        const onMouseUp = () => {
            selectionStart.current = null
        }
        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [canvas, toolMode, getCoords])

    const handleCanvasMouseDown = useCallback(
        (e: React.MouseEvent) => {
            const coords = getCoords(e.clientX, e.clientY)
            if (!coords) return
            selectionStart.current = coords
            setSelectionRect({ x: coords.x, y: coords.y, w: 0, h: 0 })
        },
        [getCoords]
    )

    const handleDone = useCallback(async () => {
        const composed = renderCanvasWithAnnotations(canvas, annotations, null)
        const rect =
            selectionRect && selectionRect.w >= 5 && selectionRect.h >= 5
                ? selectionRect
                : { x: 0, y: 0, w: composed.width, h: composed.height }
        const blob = await cropCanvasToBlob(composed, rect)
        onDone(blob)
    }, [canvas, annotations, selectionRect, onDone])

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 10002,
                bgcolor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Paper
                elevation={12}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    maxWidth: '92vw',
                    maxHeight: '92vh',
                    overflow: 'hidden',
                    borderRadius: 2
                }}
            >
                {/* canvas */}
                <Box
                    ref={containerRef}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseUp={() => {}}
                    sx={{
                        position: 'relative',
                        cursor: 'crosshair',
                        borderRadius: 1,
                        overflow: 'hidden',
                        flex: '1 1 auto',
                        minHeight: 0
                    }}
                >
                    <canvas
                        ref={displayCanvasRef}
                        width={canvas.width}
                        height={canvas.height}
                        style={{
                            maxWidth: '100%',
                            maxHeight: 'calc(92vh - 96px)',
                            display: 'block'
                        }}
                    />
                    {selectionRect && selectionRect.w > 0 && selectionRect.h > 0 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: (selectionRect.x / canvas.width) * 100 + '%',
                                top: (selectionRect.y / canvas.height) * 100 + '%',
                                width: (selectionRect.w / canvas.width) * 100 + '%',
                                height: (selectionRect.h / canvas.height) * 100 + '%',
                                border: '2px solid',
                                borderColor: 'primary.main',
                                bgcolor: 'rgba(25, 118, 210, 0.15)',
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                </Box>

                {/* bottom toolbar */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        flexShrink: 0
                    }}
                >
                    <NonInteractiveTooltip arrow placement="top" title="Drag to select crop area">
                        <IconButton size="small" color="primary">
                            <CropIcon fontSize="small" />
                        </IconButton>
                    </NonInteractiveTooltip>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={handleDone}
                    >
                        Done
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </Box>
    )
}
